import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
} from "@mui/material";
import { Email, Phone, Person, CalendarToday } from "@mui/icons-material";
import { Coach } from "../types/Coach";

interface CoachDetailsProps {
  open: boolean;
  onClose: () => void;
  coach: Coach | null;
}

const CoachDetails: React.FC<CoachDetailsProps> = ({
  open,
  onClose,
  coach,
}) => {
  if (!coach) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Person sx={{ color: "primary.main" }} />
          <Typography variant="h5" component="div" sx={{ fontWeight: "bold" }}>
            Détails de l'entraîneur
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ py: 2 }}>
          {/* Basic Information */}
          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: "primary.main", fontWeight: "bold" }}
          >
            Informations personnelles
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
              <Box
                sx={{
                  flex: "1 1 300px",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Person sx={{ color: "text.secondary" }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Nom complet
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {coach.name}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  flex: "1 1 300px",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Email sx={{ color: "text.secondary" }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {coach.mail}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
              <Box
                sx={{
                  flex: "1 1 300px",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Phone sx={{ color: "text.secondary" }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Téléphone
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {coach.number}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  flex: "1 1 300px",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Statut
                  </Typography>
                  <Chip
                    label={coach.isActive ? "Actif" : "Inactif"}
                    color={coach.isActive ? "success" : "default"}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Team Information */}
          <Divider sx={{ my: 3 }} />
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              sx={{ mb: 2, color: "primary.main", fontWeight: "bold" }}
            >
              Équipe associée
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {coach.team ? (
                <Chip
                  label={`${coach.team.name} (${coach.team.shortName})`}
                  variant="filled"
                  color="primary"
                  sx={{
                    margin: "2px",
                    "& .MuiChip-label": {
                      fontSize: "0.875rem",
                    },
                  }}
                />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 3,
                    backgroundColor: "grey.50",
                    borderRadius: 2,
                    width: "100%",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Aucune équipe associée
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Timeline Information */}
          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: "primary.main", fontWeight: "bold", mt: 4 }}
          >
            Historique
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
            <Box
              sx={{
                flex: "1 1 300px",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <CalendarToday sx={{ color: "text.secondary" }} />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Date de création
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {formatDate(coach.createdAt)}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                flex: "1 1 300px",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <CalendarToday sx={{ color: "text.secondary" }} />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Dernière modification
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {formatDate(coach.updatedAt)}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Additional Information */}
          <Box
            sx={{
              backgroundColor: "#f8f9fa",
              p: 2,
              borderRadius: 2,
              border: "1px solid #e9ecef",
              mt: 3,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              <strong>ID:</strong> {coach._id}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
            "&:hover": {
              background: "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
            },
          }}
        >
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CoachDetails;
